import { Component, OnInit } from '@angular/core';
import { IncentiveService } from '../../services/incentive.service';
import { ClinichedatabaseService } from '../../services/clinichedatabase.service';
import { ExcelBulkService } from '../../services/excel-bulk.service';
import * as moment from 'moment';

interface Clinica { code_c: string; descrizione: string; }

const HEADERS = ['Codice', 'Clinica', 'Trimestre', 'Recensioni', 'Survey', 'Malus', 'Efficienza'];
const MESI_CHIUSURA = [3, 6, 9, 12];

// Tab "Dati Trimestrali CM" dentro Bonus2025Component: data entry dei dati extra CM
// (Quality Bonus + Efficienza) su cui si basa il calcolo trimestrale. I dati sono per
// natura trimestrali (non mensili): una riga per clinica per trimestre, niente
// aggregazione/regola di maggioranza (la vecchia tab "Dati Mensili CM" chiedeva 3
// righe per trimestre con una regola 2-su-3 - sostituita da questa, un'unica riga
// per trimestre - vedi Controllers/incentive_cm_dati_trimestrali_clinicaController.cs).
@Component({
  selector: 'app-cm-dati-trimestrali-tab',
  template: `
    <div class="tab-toolbar">
      <button mat-raised-button color="primary" (click)="addRow()">+ Nuova riga</button>
      <button mat-raised-button (click)="load()">Aggiorna</button>
      <button mat-raised-button (click)="exportExcel()">Esporta Excel</button>
      <button mat-raised-button (click)="fileInput.click()">Importa Excel</button>
      <input #fileInput type="file" accept=".xlsx,.xls" style="display:none" (change)="importExcel($event)">
      <label class="mese-sel">Trimestre:
        <select [value]="quarterSel" (change)="onQuarterChange($event)">
          <option *ngFor="let q of [1,2,3,4]" [value]="q">{{quarterLabel(q)}}</option>
        </select>
        <input type="number" [value]="yearSel" (change)="onYearChange($event)" style="width:70px">
      </label>
    </div>
    <div class="hint">I valori Recensioni/Survey/Efficienza sono l'esito già deciso (Si/No) per l'intero trimestre; il Malus è l'importo già calcolato in €. Una sola riga per clinica per trimestre, nessuna somma/aggregazione (vedi guida).</div>
    <div class="spinner-container" *ngIf="showb">
      <mat-spinner></mat-spinner>
    </div>
    <table class="data-table">
      <thead>
        <tr>
          <th>Clinica</th>
          <th>Trimestre</th>
          <th>Recensioni</th>
          <th>Survey</th>
          <th>Malus</th>
          <th>Efficienza</th>
          <th>Azioni</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let row of data">
          <td>
            <select [(ngModel)]="row.CodClinica" [disabled]="!row.editing">
              <option value="" disabled>Seleziona...</option>
              <option *ngFor="let c of clinicaOptions" [value]="c.code_c">{{c.descrizione}}</option>
            </select>
          </td>
          <td>
            <ng-container *ngIf="row.editing; else trimestreLabel">
              <select [(ngModel)]="row.quarterNum">
                <option *ngFor="let q of [1,2,3,4]" [ngValue]="q">{{quarterLabel(q)}}</option>
              </select>
              <input type="number" [(ngModel)]="row.yearNum" style="width:70px">
            </ng-container>
            <ng-template #trimestreLabel>T{{row.quarterNum}} {{row.yearNum}}</ng-template>
          </td>
          <td>
            <select [(ngModel)]="row.RecensioniOk" [disabled]="!row.editing">
              <option [ngValue]="true">Si</option>
              <option [ngValue]="false">No</option>
            </select>
          </td>
          <td>
            <select [(ngModel)]="row.SurveyOk" [disabled]="!row.editing">
              <option [ngValue]="true">Si</option>
              <option [ngValue]="false">No</option>
            </select>
          </td>
          <td><input type="number" step="0.01" [(ngModel)]="row.Malus" [disabled]="!row.editing"></td>
          <td>
            <select [(ngModel)]="row.EfficienzaOk" [disabled]="!row.editing">
              <option [ngValue]="true">Si</option>
              <option [ngValue]="false">No</option>
            </select>
          </td>
          <td>
            <button *ngIf="!row.editing" mat-icon-button class="btn-edit" (click)="row.editing = true" matTooltip="Modifica"><mat-icon>edit</mat-icon></button>
            <button *ngIf="row.editing" mat-icon-button color="primary" (click)="save(row)" matTooltip="Salva"><mat-icon>save</mat-icon></button>
            <button *ngIf="row.editing" mat-icon-button (click)="cancelEdit(row)" matTooltip="Annulla modifica"><mat-icon>close</mat-icon></button>
            <button mat-icon-button color="warn" (click)="deleteRow(row)" matTooltip="Elimina"><mat-icon>delete</mat-icon></button>
          </td>
        </tr>
      </tbody>
    </table>
  `,
  styleUrls: ['./data-entry-tabs.css']
})
export class CmDatiTrimestraliTabComponent implements OnInit {
  data: any[] = [];
  allData: any[] = [];
  quarterSel: number = Math.ceil((moment().month() + 1) / 3);
  yearSel: number = moment().year();
  clinicaOptions: Clinica[] = [];
  showb = false;

  constructor(
    private incentiveService: IncentiveService,
    private clinichedatabaseService: ClinichedatabaseService,
    private excelBulk: ExcelBulkService) { }

  ngOnInit() {
    this.clinichedatabaseService.getCliniche().subscribe((c: Clinica[]) => this.clinicaOptions = c);
    this.load();
  }

  quarterLabel(q: number): string {
    switch (q) {
      case 1: return 'T1 (gen-mar)';
      case 2: return 'T2 (apr-giu)';
      case 3: return 'T3 (lug-set)';
      case 4: return 'T4 (ott-dic)';
      default: return '';
    }
  }

  private trimestreDateStr(q: number, y: number): string {
    return moment(`${y}-${String(q * 3).padStart(2, '0')}-01`).format('YYYY-MM-DD');
  }

  private snap(r: any): string {
    const { _orig, isNew, editing, ...rest } = r;
    return JSON.stringify(rest);
  }

  private hasUnsaved(): boolean {
    return this.data.some(r => r._orig === undefined || r._orig !== this.snap(r));
  }

  private requestPeriodoChange(nuovoQ: number, nuovoY: number, revert: () => void) {
    if (nuovoQ === this.quarterSel && nuovoY === this.yearSel) { return; }
    if (this.hasUnsaved() &&
        !window.confirm('Ci sono modifiche non salvate: cambiando trimestre andranno perse.\n\nOK = cambio trimestre\nAnnulla = rimango nel trimestre di lavoro')) {
      revert();
      return;
    }
    this.quarterSel = nuovoQ;
    this.yearSel = nuovoY;
    this.load();
  }

  onQuarterChange(event: any) {
    const nuovo = Number(event.target.value);
    this.requestPeriodoChange(nuovo, this.yearSel, () => { event.target.value = String(this.quarterSel); });
  }

  onYearChange(event: any) {
    const nuovo = Number(event.target.value);
    if (!nuovo) { event.target.value = String(this.yearSel); return; }
    this.requestPeriodoChange(this.quarterSel, nuovo, () => { event.target.value = String(this.yearSel); });
  }

  private periodoFiltro(): string {
    return moment(this.trimestreDateStr(this.quarterSel, this.yearSel)).format('YYYY-MM');
  }

  applyFilter() {
    const p = this.periodoFiltro();
    this.data = this.allData.filter(r => r.Trimestre && moment(r.Trimestre).format('YYYY-MM') === p);
  }

  load() {
    this.showb = true;
    this.incentiveService.getCmDatiTrimestrali().subscribe((res: any[]) => {
      this.allData = (res || []).map(r => ({
        ...r,
        quarterNum: Math.round((moment(r.Trimestre).month() + 1) / 3),
        yearNum: moment(r.Trimestre).year()
      }));
      this.allData.forEach(r => r._orig = this.snap(r));
      this.applyFilter();
      this.showb = false;
    }, () => { this.showb = false; });
  }

  addRow() {
    this.data.unshift({
      editing: true, CodClinica: '', quarterNum: this.quarterSel, yearNum: this.yearSel,
      RecensioniOk: false, SurveyOk: false, Malus: 0, EfficienzaOk: false, isNew: true
    });
  }

  save(row: any) {
    if (!row.CodClinica || !row.quarterNum || !row.yearNum) {
      window.alert('Clinica e Trimestre sono obbligatori.');
      return;
    }
    const payload = {
      CodClinica: row.CodClinica,
      Trimestre: this.trimestreDateStr(row.quarterNum, row.yearNum),
      RecensioniOk: !!row.RecensioniOk,
      SurveyOk: !!row.SurveyOk,
      Malus: row.Malus,
      EfficienzaOk: !!row.EfficienzaOk
    };
    this.incentiveService.upsertCmDatiTrimestrali(payload).subscribe(() => {
      window.alert('Salvato.');
      // Se la riga salvata è di un trimestre diverso da quello selezionato, allinea
      // il selettore cosi' il ricaricamento la mostra subito (stesso bug gia' visto
      // sulla vecchia tab mensile: il filtro non si aggiorna da solo dopo un save).
      this.quarterSel = row.quarterNum;
      this.yearSel = row.yearNum;
      this.load();
    }, err => window.alert('Errore nel salvataggio. ' + (err && err.message)));
  }

  cancelEdit(row: any) {
    if (row._orig === undefined) {
      this.data = this.data.filter(r => r !== row);
      return;
    }
    Object.assign(row, JSON.parse(row._orig));
    row.editing = false;
  }

  deleteRow(row: any) {
    if (row.isNew) {
      this.data = this.data.filter(r => r !== row);
      return;
    }
    if (!window.confirm('Confermi la cancellazione della riga?')) { return; }
    this.incentiveService.deleteCmDatiTrimestrali(row.CodClinica, moment(row.Trimestre).format('YYYY-MM-DD')).subscribe(() => this.load());
  }

  exportExcel() {
    const rows = this.data.map(r => ({
      Codice: parseInt(r.CodClinica, 10) || r.CodClinica,
      Clinica: this.getDescClinica(r.CodClinica),
      Trimestre: this.trimestreDateStr(r.quarterNum, r.yearNum),
      Recensioni: r.RecensioniOk ? 'Si' : 'No',
      Survey: r.SurveyOk ? 'Si' : 'No',
      Malus: r.Malus,
      Efficienza: r.EfficienzaOk ? 'Si' : 'No'
    }));
    this.excelBulk.exportTemplate(rows, HEADERS, 'dati_base_bonus_trimestrale');
  }

  private getDescClinica(codClinica: string): string {
    const c = this.clinicaOptions.find(x => x.code_c === codClinica);
    return c ? c.descrizione : codClinica;
  }

  // Estrae il codice clinica (es. "001") dal testo della colonna "Clinica" del file
  // (es. "001 - Orbassano", "323- Trapani" - spaziatura non sempre coerente nel file
  // di origine), zero-paddato a 3 cifre.
  private codClinicaDaTesto(testo: string): string | null {
    const match = String(testo || '').trim().match(/^(\d+)/);
    if (!match) { return null; }
    return match[1].padStart(3, '0');
  }

  async importExcel(event: any) {
    const file = event.target.files && event.target.files[0];
    if (!file) { return; }
    try {
      const rows = await this.excelBulk.readSheetWithHeaderDetection(file, ['Clinica', 'Trimestre', 'Recensioni', 'Survey', 'Malus', 'Efficienza']);
      if (!rows.length) {
        window.alert('Il file non contiene righe.');
        return;
      }
      if (!window.confirm(`Importare ${rows.length} righe?`)) { return; }

      this.showb = true;
      let ok = 0, ko = 0;
      const trimestriImportati = new Set<string>();
      const motivi: string[] = [];
      let rigaNum = 0;
      for (const r of rows) {
        rigaNum++;
        const clinicaRaw = r['Clinica'];
        const trimestreRaw = r['Trimestre'];
        const trimestreVuoto = trimestreRaw === null || trimestreRaw === undefined || String(trimestreRaw).trim() === '';
        const codClinica = this.codClinicaDaTesto(clinicaRaw);
        // Trimestre vuoto/mancante nel file: usa il trimestre selezionato in testata
        // invece di scartare la riga (richiesta esplicita), cosi' si puo' importare un
        // file con solo Clinica/Recensioni/Survey/Malus/Efficienza valorizzati.
        const data = trimestreVuoto
          ? moment(this.trimestreDateStr(this.quarterSel, this.yearSel)).toDate()
          : this.excelBulk.parseExcelDate(trimestreRaw);
        const mese = data ? moment(data).month() + 1 : null;

        if (!codClinica) {
          ko++;
          motivi.push(`Riga ${rigaNum}: clinica non riconosciuta ("${clinicaRaw}")`);
          continue;
        }
        if (!data) {
          ko++;
          motivi.push(`Riga ${rigaNum}: Trimestre non valido come data ("${trimestreRaw}")`);
          continue;
        }
        if (!MESI_CHIUSURA.includes(mese)) {
          ko++;
          motivi.push(`Riga ${rigaNum}: Trimestre non di chiusura ("${trimestreRaw}", mese ${mese} - atteso marzo/giugno/settembre/dicembre)`);
          continue;
        }

        const payload = {
          CodClinica: codClinica,
          // Normalizzato al 1° giorno del mese di chiusura, come fa trimestreDateStr()
          // per l'inserimento manuale: il backend cerca sempre il match su quella data
          // esatta (vedi PostCalcTrimestrale), quindi un giorno diverso dal 1° (es. il
          // file ha 30/09) farebbe sparire la riga dal calcolo senza errori visibili.
          Trimestre: moment(data).startOf('month').format('YYYY-MM-DD'),
          RecensioniOk: this.excelBulk.parseExcelBool(r['Recensioni']),
          SurveyOk: this.excelBulk.parseExcelBool(r['Survey']),
          Malus: r['Malus'] !== '' ? Number(r['Malus']) : 0,
          EfficienzaOk: this.excelBulk.parseExcelBool(r['Efficienza'])
        };
        try {
          await this.incentiveService.upsertCmDatiTrimestrali(payload).toPromise();
          ok++;
          trimestriImportati.add(moment(payload.Trimestre).format('YYYY-MM'));
        } catch (err) {
          ko++;
          motivi.push(`Riga ${rigaNum}: errore salvataggio (${(err && err.message) || 'errore sconosciuto'})`);
        }
        await this.excelBulk.wait(200);
      }
      this.showb = false;

      let msg = `Import completato: ${ok} righe salvate, ${ko} scartate/errore.`;
      if (motivi.length) {
        const MAX_MOTIVI = 20;
        const elenco = motivi.slice(0, MAX_MOTIVI).join('\n');
        const extra = motivi.length > MAX_MOTIVI ? `\n... e altre ${motivi.length - MAX_MOTIVI} righe` : '';
        msg += `\n\nMotivi esclusione:\n${elenco}${extra}`;
      }

      if (trimestriImportati.size === 1) {
        const t = Array.from(trimestriImportati)[0];
        this.quarterSel = Math.round((moment(t + '-01').month() + 1) / 3);
        this.yearSel = moment(t + '-01').year();
        msg += '\n\nTrimestre visualizzato aggiornato.';
      } else if (trimestriImportati.size > 1) {
        msg += '\n\nAttenzione: il file conteneva più trimestri - seleziona quello che vuoi visualizzare.';
      }
      window.alert(msg);
      this.load();
    } catch (err) {
      this.showb = false;
      window.alert('Errore nella lettura del file Excel. ' + (err && err.message ? err.message : ''));
    } finally {
      event.target.value = '';
    }
  }
}
